import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import { RootState } from '../../../../shared/state/root-reducer';
import '../../../../scss/pages/patients/dispute-response.scss';
import paymentIcon from '../../../../scss/pages/patients/payment-icon.png'
import smsIcon from '../../../../scss/pages/patients/sms-icon.png'
import spaIcon from '../../../../scss/pages/patients/spa-icon.png'
import { DetailedLedger } from "@finpay-development/shared-platform/lib/esm/shared/model/ledger";
import { PatientChampion } from "@finpay-development/shared-platform/lib/esm/patient/components/models/patient-champion";
import { PrimaryContact } from "@finpay-development/shared-platform/lib/esm/shared/model/primary-contact";
import { getPayment } from "@finpay-development/shared-platform/lib/esm/payment-hub/services/payment-service";

function DisputeResponse() {
    const params = useParams()
    const disputeId = params.did
    const currentDate = new Date().toLocaleDateString()
    const [disputeTransaction, setDisputeTransaction] = useState<DetailedLedger | undefined>(undefined)
    const [originalPaymentTransaction, setOriginalPaymentTransaction] = useState<DetailedLedger | undefined>(undefined)
    const [bankName, setBankName] = useState<string | undefined>(undefined)
    const stateFields = {
        selectedPatient: useSelector((state: RootState) => {
            return state.patientContext.selectedPatient
        }),
        selectedPatientEncounter: useSelector((state: RootState) => {
            return state.patientContext.selectedEncounter
        }),
    }
    const { selectedPatient, selectedPatientEncounter } = stateFields;
    const transactions = selectedPatientEncounter?.encounterLedger?.ledger as DetailedLedger[] | undefined
    const patient = selectedPatient
    const contact = patient?.contact
    const authDocCreatedDt = selectedPatientEncounter?.instanceCreateDt
    const clientName = selectedPatientEncounter?.clientName
    const signedInstall = selectedPatientEncounter?.authorizationDocumentStatus?.installmentDocuments?.find((doc) => doc?.lastUpdateDt)
    const spaSigned = selectedPatientEncounter?.authorizationDocumentStatus?.spaDocuments?.find((doc) => doc?.lastUpdateDt)
    const patientChampion = selectedPatientEncounter?.patientChampion
    const selectedPatientChampion = patientChampion.find((champion) => disputeTransaction?.patientEncounterId === champion?.patientEncounterId)

    useEffect(() => {
        if (transactions && disputeId) {
            const findTransaction = transactions.find((transaction) => {
                const paymentDispute = transaction
                const paymentDisputeId = paymentDispute?.dispute?.paymentDisputeId
                return String(paymentDisputeId) === String(disputeId)
            })
            setDisputeTransaction(findTransaction)
        }
    }, [transactions, disputeId])

    const originalPaymentId = disputeTransaction?.dispute?.paymentId

    useEffect(() => {
        if (transactions && originalPaymentId) {
            const findPayment = transactions.find((transaction) => {
                const payment = transaction.payment
                const paymentId = payment?.paymentId
                return String(paymentId) === String(originalPaymentId)
            })
            setOriginalPaymentTransaction(findPayment)
        }
    }, [transactions, disputeTransaction])

    const paymentOriginDate = originalPaymentTransaction?.payment?.paymentInitDt

    useEffect(() => {
        if (originalPaymentId) {
            getPayment({ paymentId: originalPaymentId })
                .then((payment) => {
                    if (payment?.userPaymentMethod?.bankName) {
                        setBankName(payment.userPaymentMethod.bankName)
                    }
                })
                .catch((err) => {
                    console.error('Error fetching payment method details:', err)
                })
        }
    }, [originalPaymentId])


    function formattedDate(date: string | Date): string {
        const localDate = new Date(date);

        const formatted = localDate.toLocaleString('en-US', {
            timeZoneName: 'short',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });

        return formatted.replace(',', '');
    }

    const renderContactInfo = (
        person: PatientChampion | PrimaryContact,
        date?: string | Date | null | undefined,
        testId?: string
    ) => {
        const c = ('contact' in person ? person.contact : person);

        return (
            <>
                <h3>{c?.firstName ?? ''} {c?.lastName ?? ''}</h3>
                <h3>{c?.primPhoneNum ?? ''}</h3>
                <h3>{c?.email ?? ''}</h3>
                {date && (
                    <h3 {...(testId ? ({ ['test-id']: testId } as any) : {})}>
                        {formattedDate(date)}
                    </h3>
                )}
            </>
        );
    };

    if (transactions && transactions[0]?.payment?.paymentId === 0) {
        return <div className="dispute-not-found">
            <h1>Dispute Response Not Available</h1>
            <p>The requested dispute informmation could not be found. Please navigate back to the Instance of Care.</p>
        </div>
    }
    return (
        <div className="dispute-doc">
            <div className="dispute-header">
                <h1>Payment Consent Acknowledgement Audit Trail</h1>
                <h3>Client: {clientName}</h3>
                <h3>Date Generated: {currentDate}</h3>
                <h3>Status: {signedInstall?.lastUpdateDt || spaSigned?.lastUpdateDt || originalPaymentId ? "Acknowledged" : "Sent"}</h3>
            </div>

            <h1 className="dispute-mini-header">Consent Acknowledgment History:</h1>

            <div>
                {authDocCreatedDt && (
                    <div className="dispute-box">
                        <div className="dispute-box-icon">
                            <img src={smsIcon} alt="SMS Icon" />
                        </div>
                        <div className="dispute-box-content" test-id="sms-text-sent">
                            <h2>SMS/Text sent from FinPay to:</h2>
                            {selectedPatientChampion ? (
                                renderContactInfo(selectedPatientChampion, authDocCreatedDt)
                            ) : (
                                contact && renderContactInfo(contact, authDocCreatedDt)
                            )}
                        </div>
                    </div>
                )}
                {(signedInstall || spaSigned) ? (
                    <div className="dispute-box">
                        <div className="dispute-box-icon" test-id="SPA-section">
                            <img src={spaIcon} alt="SPA Icon" />
                        </div>
                        <div className="dispute-box-content">
                            <h2>Single Pay Agreement Reviewed and Acknowledged on FinPay by:</h2>
                            {selectedPatientChampion ? renderContactInfo(selectedPatientChampion, signedInstall?.lastUpdateDt || spaSigned?.lastUpdateDt) : (
                                contact && renderContactInfo(contact, signedInstall?.lastUpdateDt || spaSigned?.lastUpdateDt)
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="dispute-box">
                        <div className="dispute-box-icon" test-id="SPA-w-POD">
                            <img src={spaIcon} alt="SPA Icon" />
                        </div>
                        <div className="dispute-box-content">
                            <h2>Single Pay Agreement Reviewed and Acknowledged on FinPay by:</h2>
                            {selectedPatientChampion ? (
                                renderContactInfo(selectedPatientChampion, paymentOriginDate, "payment-origin-date")
                            ) : (
                                contact && renderContactInfo(contact, paymentOriginDate, "payment-origin-date")
                            )}
                        </div>
                    </div>
                )}

                <div className="dispute-box">
                    <div className="dispute-box-icon">
                        <img src={paymentIcon} alt="Payment Icon" />
                    </div>
                    <div className="dispute-box-content" test-id="payment-made-section">
                        <h2>Payment Made and Completed on FinPay by:</h2>
                        {selectedPatientChampion ? (
                            renderContactInfo(selectedPatientChampion, paymentOriginDate, "payment-origin-date")
                        ) : (
                            contact && renderContactInfo(contact, paymentOriginDate, "payment-origin-date")
                        )}
                        {bankName && (
                            <h3 test-id="payment-method-bank-name">
                                Bank Name: {bankName}
                            </h3>
                        )}
                    </div>
                </div>
            </div>
        </div>

    )
}

export default DisputeResponse